import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import API from '../../api';
import { useLang } from '../../LangContext';
import ChatWindow from '../../components/ChatWindow';
import { parseColors, categoryDisplayName } from '../../constants';
import { addToCart, getCartKey } from './cartUtils';

const Reveal = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay }}
    >
      {children}
    </motion.div>
  );
};

const TibebPattern = () => (
  <svg width="100%" height="100%" style={{ position: 'absolute', top: 0, left: 0, zIndex: 0, opacity: 0.04, pointerEvents: 'none' }}>
    <pattern id="tibeb" x="0" y="0" width="120" height="120" patternUnits="userSpaceOnUse">
      <path d="M60 0 L120 60 L60 120 L0 60 Z" fill="none" stroke="currentColor" strokeWidth="0.5" />
      <circle cx="60" cy="60" r="15" fill="none" stroke="currentColor" strokeWidth="0.5" />
      <path d="M0 0 L120 120 M120 0 L0 120" stroke="currentColor" strokeWidth="0.2" opacity="0.5" />
    </pattern>
    <rect width="100%" height="100%" fill="url(#tibeb)" />
  </svg>
);

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLang();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [toast, setToast] = useState('');
  const [toastType, setToastType] = useState('success');
  const [showChat, setShowChat] = useState(false);
  const [activePhoto, setActivePhoto] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [lightbox, setLightbox] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [addedFeedback, setAddedFeedback] = useState(false);
  const [liked, setLiked] = useState(() => {
    try { return (JSON.parse(localStorage.getItem('bazaar_liked_products')) || []).includes(Number(id)); } catch { return false; }
  });
  const [viewCount, setViewCount] = useState(0);
  const [likeCount, setLikeCount] = useState(0);

  useEffect(() => {
    API.get('/bazaar/products/' + id)
      .then(r => {
        setProduct(r.data);
        setViewCount(Number(r.data.views) || 0);
        setLikeCount(Number(r.data.likes) || 0);
      })
      .catch(err => {
        if (err.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [id]);

  const toggleLike = () => {
    const next = !liked;
    setLiked(next);
    setLikeCount(c => Math.max(0, c + (next ? 1 : -1)));
    try {
      const ids = JSON.parse(localStorage.getItem('bazaar_liked_products')) || [];
      const filtered = ids.filter(x => x !== Number(id));
      if (next) filtered.push(Number(id));
      localStorage.setItem('bazaar_liked_products', JSON.stringify(filtered));
    } catch { /* ignore */ }
    API.post(`/bazaar/products/${id}/like`, { liked: next })
      .then(r => { if (typeof r.data?.likes === 'number') setLikeCount(r.data.likes); })
      .catch(() => {});
  };

  const handleKeyDown = useCallback((e) => {
    if (!lightbox) return;
    const photos = product?.photos || [];
    if (e.key === 'ArrowRight') setActivePhoto(p => (p + 1) % photos.length);
    if (e.key === 'ArrowLeft') setActivePhoto(p => (p - 1 + photos.length) % photos.length);
    if (e.key === 'Escape') setLightbox(false);
  }, [lightbox, product]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const showToast = (msg, type = 'success') => {
    setToast(msg);
    setToastType(type);
    setTimeout(() => setToast(''), 2500);
  };

  const handleAddToCart = () => {
    const token = localStorage.getItem('buyerToken') || localStorage.getItem('userToken');
    if (!token) { setShowLoginPrompt(true); return; }

    const prod = product;
    // Must go through getCartKey: the key is user-scoped, and hardcoding
    // `bazaar_cart_<sellerId>` wrote to a key nothing else in the app reads,
    // so items added here never appeared in the cart, navbar badge, or checkout.
    const key = getCartKey(prod.seller_id);
    if (!key) { setShowLoginPrompt(true); return; }

    let cart = [];
    try { cart = JSON.parse(localStorage.getItem(key)) || []; } catch { cart = []; }
    if (!Array.isArray(cart)) cart = [];
    cart = addToCart(cart, prod, quantity);
    localStorage.setItem(key, JSON.stringify(cart));
    setAddedFeedback(true);
    setTimeout(() => setAddedFeedback(false), 2000);
    showToast(`${quantity} × ${prod.name} ${t.pdWishlistToast}`);
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title: product.name, url: window.location.href }).catch(() => {});
    } else {
      navigator.clipboard.writeText(window.location.href);
      showToast(t.pdLinkCopied);
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 100, color: 'var(--primary)', fontWeight: 700 }}>{t.loading}</div>;
  }

  if (notFound || !product) {
    return (
      <div className="page" style={{ paddingTop: 100 }}>
        <div className="hero-gradient" style={{ height: '30vh' }} />
        <Link to="/bazaar" style={{ color: 'var(--primary)', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 32 }}>{t.backToBazaar}</Link>
        <div style={{ textAlign: 'center', padding: '60px 24px' }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>🔍</div>
          <p style={{ color: 'var(--text-muted)' }}>{t.pdNotFound}</p>
          <Link to="/bazaar" style={{ color: 'var(--primary)', fontWeight: 800, textDecoration: 'none', marginTop: 16, display: 'inline-block' }}>{t.pdBrowseBazaar}</Link>
        </div>
      </div>
    );
  }

  const photos = product.photos || [];
  const outOfStock = Number(product.stock_quantity) === 0;
  const lowStock = !outOfStock && Number(product.stock_quantity) <= 5;
  const maxQty = Math.min(Number(product.stock_quantity) || 1, 20);
  const hasDiscount = product.discount_price && Number(product.discount_price) < Number(product.price);
  const displayPrice = hasDiscount ? Number(product.discount_price) : Number(product.price);

  return (
    <div className="page product-detail-page" style={{ paddingTop: 100 }}>
      <div className="hero-gradient" style={{ height: '30vh' }} />
      <TibebPattern />

      {/* Login prompt */}
      {showLoginPrompt && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(30, 41, 59, 0.8)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 24 }}
          onClick={() => setShowLoginPrompt(false)}>
          <div className="creative-card" style={{ padding: '48px 32px', maxWidth: 400, width: '100%', textAlign: 'center', border: 'none' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 48, marginBottom: 20 }}>🔐</div>
            <h2 style={{ fontSize: 24, fontWeight: 900, marginBottom: 12 }}>{t.pdSignInTitle}</h2>
            <p style={{ color: 'var(--text-muted)', fontSize: 16, marginBottom: 32, lineHeight: 1.6 }}>{t.pdSignInDesc}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <button onClick={() => navigate('/login')} className="btn-primary" style={{ width: '100%', justifyContent: 'center' }}>{t.pdSignInCta}</button>
              <button onClick={() => navigate('/register/buyer')} className="btn-outline" style={{ width: '100%', justifyContent: 'center' }}>{t.pdRegisterBuyer}</button>
              <button onClick={() => setShowLoginPrompt(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 17, fontWeight: 700, cursor: 'pointer', marginTop: 8 }}>{t.pdCancel}</button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightbox && photos.length > 0 && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(30, 41, 59, 0.95)', backdropFilter: 'blur(10px)', zIndex: 9998, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 24 }}
          onClick={() => setLightbox(false)}>
          <button onClick={() => setLightbox(false)} style={{ position: 'absolute', top: 32, right: 32, background: '#fff', border: 'none', width: 48, height: 48, borderRadius: '50%', fontSize: 24, cursor: 'pointer', zIndex: 1 }}>✕</button>
          <motion.img initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} src={photos[activePhoto]?.url} alt={product.name} style={{ maxWidth: '90vw', maxHeight: '80vh', objectFit: 'contain', borderRadius: 20, boxShadow: '0 50px 100px rgba(0,0,0,0.3)' }} onClick={e => e.stopPropagation()} />
        </div>
      )}

      {/* Breadcrumb */}
      <nav style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 32, fontSize: 17, fontWeight: 700 }}>
        <Link to="/bazaar" style={{ color: 'var(--primary)', textDecoration: 'none' }}>{t.bazaarTitle}</Link>
        <span style={{ color: 'var(--text-muted)' }}>›</span>
        <Link to={`/bazaar/seller/${product.seller_id}`} style={{ color: 'var(--primary)', textDecoration: 'none' }}>{product.seller_name}</Link>
        <span style={{ color: 'var(--text-muted)' }}>›</span>
        <span style={{ color: 'var(--text-muted)' }}>{product.name}</span>
      </nav>

      <div className="pd-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 0.8fr)', gap: 64, alignItems: 'start' }}>
        {/* Gallery */}
        <Reveal>
          <div className="pd-gallery-wrap" style={{ position: 'sticky', top: 120 }}>
            <div 
              className="creative-card"
              onClick={() => photos.length > 0 && setLightbox(true)}
              style={{ padding: 0, overflow: 'hidden', cursor: 'zoom-in', aspectRatio: '1/1', background: 'var(--bg-subtle)' }}
            >
              {photos.length > 0 ? (
                <img
                  src={photos[activePhoto]?.url}
                  alt={product.name}
                  style={{ width: '100%', height: '100%', objectFit: 'contain', opacity: outOfStock ? 0.5 : 1 }}
                />
              ) : (
                <div style={{ width: '100%', height: '100%', background: 'var(--bg-subtle)' }} />
              )}
              {hasDiscount && (
                <div style={{ position: 'absolute', top: 16, left: 16, background: '#ef4444', color: '#fff', fontWeight: 900, fontSize: 17, padding: '8px 18px', borderRadius: 100, boxShadow: '0 8px 20px rgba(239, 68, 68, 0.3)' }}>
                  {t.pdOffPct(Math.round((1 - Number(product.discount_price) / Number(product.price)) * 100))}
                </div>
              )}
              {outOfStock && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.4)', backdropFilter: 'blur(4px)' }}>
                  <span style={{ background: '#ef4444', color: '#fff', fontWeight: 900, fontSize: 20, padding: '12px 32px', borderRadius: 100, textTransform: 'uppercase' }}>{t.pdSoldOut}</span>
                </div>
              )}
            </div>

            {photos.length > 1 && (
              <div className="pd-thumbs" style={{ display: 'flex', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
                {photos.map((ph, i) => (
                  <button
                    key={i}
                    onClick={() => setActivePhoto(i)}
                    className="pd-thumb"
                    style={{
                      width: 80, height: 80, borderRadius: 16, overflow: 'hidden', padding: 0, background: 'var(--bg-main)',
                      border: `2px solid ${activePhoto === i ? 'var(--primary)' : 'rgba(0,0,0,0.05)'}`,
                      transition: 'all 0.2s', cursor: 'pointer', flexShrink: 0,
                    }}
                  >
                    <img src={ph.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </button>
                ))}
              </div>
            )}
          </div>
        </Reveal>

        {/* Info */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
          <Reveal delay={0.1}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
              <span className="section-tag" style={{ margin: 0 }}>
                {categoryDisplayName(t, product.category)}
                {product.subcategory && <span style={{ marginLeft: 8, fontSize: 17, fontWeight: 700 }}>› {product.subcategory}</span>}
              </span>
              <button onClick={handleShare} className="btn-outline" style={{ padding: '8px 20px', fontSize: 16 }}>{t.pdShare}</button>
            </div>

            {/* Color dots */}
            {(() => {
              const dots = parseColors(product.colors);
              if (dots.length === 0) return null;
              return (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>{t.pdAvailableColors}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    {dots.map((d, i) =>
                      d.all ? (
                        <span key={i} title={t.cardAllColorsTitle} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 14px', borderRadius: 100, background: 'var(--bg-subtle)', border: '1px solid rgba(127,127,127,0.25)', fontWeight: 700, fontSize: 17 }}>
                          <span style={{ width: 18, height: 18, borderRadius: '50%', border: '1px solid rgba(0,0,0,0.15)', background: 'conic-gradient(#EF4444,#EAB308,#22C55E,#3B82F6,#8B5CF6,#EF4444)', display: 'inline-block', flexShrink: 0 }} />
                          {t.pdAllColors}
                        </span>
                      ) : (
                        <span key={i} title={d.name} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 14px', borderRadius: 100, background: 'var(--bg-subtle)', border: '1px solid rgba(127,127,127,0.25)', fontWeight: 700, fontSize: 17 }}>
                          <span style={{ width: 18, height: 18, borderRadius: '50%', background: d.hex || '#e5e7eb', border: `1px solid ${d.hex === '#FFFFFF' || d.hex === '#EDE0C8' ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.12)'}`, display: 'inline-block', flexShrink: 0 }} />
                          {d.name}
                        </span>
                      )
                    )}
                  </div>
                </div>
              );
            })()}

            <h1 style={{ fontSize: 'clamp(22px, 3.5vw, 30px)', fontWeight: 900, marginBottom: 16, color: 'var(--secondary)', lineHeight: 1.2 }}>{product.name}</h1>

            <div className="pd-price-row" style={{ display: 'flex', alignItems: 'baseline', gap: 16, marginBottom: 24 }}>
              <span className="pd-price" style={{ fontSize: 36, fontWeight: 900, color: hasDiscount ? '#ef4444' : 'var(--primary)' }}>
                {hasDiscount ? (
                  <>
                    <span style={{ textDecoration: 'line-through', color: 'var(--text-muted)', fontSize: 24, fontWeight: 600, marginRight: 12 }}>ETB {Number(product.price).toLocaleString()}</span>
                    ETB {Number(product.discount_price).toLocaleString()}
                  </>
                ) : (
                  <>ETB {Number(product.price).toLocaleString()}</>
                )}
              </span>
              {lowStock && <span style={{ color: '#f59e0b', fontWeight: 800, fontSize: 17 }}>🔥 {t.pdOnlyLeft(product.stock_quantity)}</span>}
            </div>

            {/* Likes & Views */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
              <button onClick={toggleLike} aria-label={liked ? t.pdUnlike : t.pdLikeAction}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 18px',
                  borderRadius: 100, cursor: 'pointer', fontSize: 17, fontWeight: 800,
                  background: liked ? 'rgba(239,68,68,0.1)' : 'var(--bg-subtle)',
                  border: `1px solid ${liked ? 'rgba(239,68,68,0.4)' : 'rgba(127,127,127,0.3)'}`,
                  color: liked ? '#ef4444' : 'var(--text-muted)',
                  transition: 'all 0.2s',
                }}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill={liked ? '#ef4444' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
                {likeCount} {likeCount === 1 ? t.pdLikeOne : t.pdLikes}
              </button>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 18px',
                borderRadius: 100, fontSize: 17, fontWeight: 800,
                background: 'var(--bg-subtle)', border: '1px solid rgba(127,127,127,0.3)', color: 'var(--text-muted)',
              }}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                {viewCount} {viewCount === 1 ? t.pdViewOne : t.pdViews}
              </span>
            </div>

            <div className="creative-card" style={{ padding: 24, background: 'var(--bg-subtle)', border: 'none' }}>
              <h3 style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>{t.pdDescription}</h3>
              <p style={{ fontSize: 16, color: 'var(--text-main)', lineHeight: 1.7, margin: 0 }}>{product.description || t.pdNoDescription}</p>
            </div>

            {/* Package items */}
            {Number(product.is_package) === 1 && product.package_items && (
              <div className="creative-card" style={{ padding: 24, border: '1px solid rgba(139, 92, 246, 0.15)', background: 'rgba(139, 92, 246, 0.03)' }}>
                <h3 style={{ fontSize: 17, fontWeight: 800, color: '#8b5cf6', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>
                  📦 {t.pdWhatsIncluded}
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {(() => {
                    try {
                      const items = typeof product.package_items === 'string' ? JSON.parse(product.package_items) : product.package_items;
                      return items.map((item, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: 'var(--bg-subtle)', borderRadius: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                          <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(139, 92, 246, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, fontWeight: 900, color: '#8b5cf6', flexShrink: 0 }}>
                            {i + 1}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 700, color: 'var(--secondary)', fontSize: 17 }}>{item.name}</div>
                          </div>
                          <div style={{ background: 'rgba(139, 92, 246, 0.08)', padding: '4px 12px', borderRadius: 20, fontWeight: 800, fontSize: 17, color: '#8b5cf6' }}>
                            ×{item.quantity}
                          </div>
                        </div>
                      ));
                    } catch { return null; }
                  })()}
                </div>
              </div>
            )}
          </Reveal>

          {/* Seller Card */}
          <Reveal delay={0.2}>
            <div className="creative-card" style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
              <div style={{ width: 60, height: 60, background: 'var(--primary)', color: '#fff', borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, fontWeight: 900 }}>
                {product.seller_name?.charAt(0)}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 17, color: 'var(--text-muted)', fontWeight: 800, textTransform: 'uppercase', marginBottom: 2 }}>{t.pdSoldBy}</div>
                <Link to={`/bazaar/seller/${product.seller_id}`} style={{ fontSize: 18, fontWeight: 900, color: 'var(--secondary)', textDecoration: 'none' }}>{product.seller_name}</Link>
              </div>
              <Link to={`/bazaar/seller/${product.seller_id}`} className="btn-outline" style={{ padding: '10px 20px', fontSize: 16 }}>{t.pdStore}</Link>
            </div>
          </Reveal>

          {/* Quantity & Actions */}
          {!outOfStock && (
            <Reveal delay={0.3}>
              <div className="pd-qty-row" style={{ display: 'flex', alignItems: 'center', gap: 24, marginBottom: 24 }}>
                <div>
                  <h3 style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 12 }}>{t.pdQuantity}</h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <button onClick={() => setQuantity(q => Math.max(1, q - 1))} style={qtyBtnStyle}>-</button>
                    <span style={{ fontSize: 20, fontWeight: 900, minWidth: 30, textAlign: 'center' }}>{quantity}</span>
                    <button onClick={() => setQuantity(q => Math.min(maxQty, q + 1))} style={qtyBtnStyle}>+</button>
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 12 }}>{t.pdSubtotal}</h3>
                  <div style={{ fontSize: 24, fontWeight: 900, color: 'var(--secondary)' }}>ETB {(displayPrice * quantity).toLocaleString()}</div>
                </div>
              </div>
            </Reveal>
          )}

          <Reveal delay={0.4}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {outOfStock ? (
                <div className="btn-outline" style={{ width: '100%', justifyContent: 'center', opacity: 0.5, cursor: 'not-allowed' }}>{t.pdSoldOut}</div>
              ) : (
                <button onClick={handleAddToCart} className="btn-primary" style={{ width: '100%', justifyContent: 'center', padding: '18px 0', fontSize: 16 }}>
                  {addedFeedback ? `✓ ${t.pdAddedToWishlist}` : `${t.pdAddToWishlist} — ETB ${(displayPrice * quantity).toLocaleString()}`}
                </button>
              )}
              <button onClick={() => setShowChat(true)} className="btn-outline" style={{ width: '100%', justifyContent: 'center', padding: '16px 0' }}>
                💬 {t.pdChatWithSeller}
              </button>
            </div>
          </Reveal>

          {/* Delivery Info */}
          <Reveal delay={0.5}>
            <div className="grid-3">
              <InfoSmall icon="🚚" label={t.pdDeliveryTitle} sub={t.pdDeliveryDesc} />
              <InfoSmall icon="🛡️" label={t.pdSecureTitle} sub={t.pdSecureDesc} />
              <InfoSmall icon="✅" label={t.pdAuthenticTitle} sub={t.pdAuthenticDesc} />
            </div>
          </Reveal>
        </div>
      </div>

      {toast && (
        <motion.div initial={{ y: 100 }} animate={{ y: 0 }} style={{ position: 'fixed', bottom: 40, right: 40, background: 'rgba(15,23,42,0.95)', color: '#fff', padding: '16px 32px', borderRadius: 20, fontWeight: 800, zIndex: 9999 }}>
          {toast}
        </motion.div>
      )}

      {showChat && <ChatWindow sellerId={product.seller_id} sellerName={product.seller_name} productId={product.id} onClose={() => setShowChat(false)} />}

      <style>{`
        @media (max-width: 900px) {
          .pd-grid { grid-template-columns: 1fr !important; gap: 32px !important; }
          .pd-gallery-wrap { position: static !important; }
        }
        @media (max-width: 768px) {
          .page.product-detail-page { padding-top: 76px !important; }
          .pd-grid { gap: 24px !important; }
          .pd-price { font-size: 28px !important; }
          .pd-price-row span span { font-size: 18px !important; margin-right: 8px !important; }
          .pd-thumb { width: 64px !important; height: 64px !important; }
          .pd-qty-row { gap: 16px !important; }
        }
        @media (max-width: 480px) {
          .pd-grid { gap: 20px !important; padding: 0 !important; }
          .page.product-detail-page .hero-gradient { height: 20vh !important; }
          .pd-price { font-size: 24px !important; }
          .pd-price-row span span { font-size: 16px !important; }
          .pd-thumb { width: 56px !important; height: 56px !important; border-radius: 10px !important; }
          .pd-thumbs { gap: 8px !important; }
          .pd-qty-row { flex-wrap: wrap !important; gap: 16px !important; }
          .pd-qty-row > div { flex: 1 1 40% !important; }
          .grid-3 { grid-template-columns: 1fr !important; }
          .creative-card { border-radius: 12px !important; }
        }
      `}</style>
    </div>
  );
}

const InfoSmall = ({ icon, label, sub }) => (
  <div className="creative-card" style={{ padding: 16, textAlign: 'center' }}>
    <div style={{ fontSize: 24, marginBottom: 8 }}>{icon}</div>
    <div style={{ fontSize: 17, fontWeight: 900, color: 'var(--secondary)' }}>{label}</div>
    <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 700 }}>{sub}</div>
  </div>
);

const qtyBtnStyle = {
  width: 40, height: 40, borderRadius: 12, border: '1px solid rgba(0,0,0,0.1)', background: 'var(--bg-main)',
  fontSize: 20, fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center'
};
